import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, Navigation, Award } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, EmptyState, UserAvatar, SpecPill, AvailabilityPill, Pill, VipBadge } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { SPECS, CERT_OPTIONS } from "@/lib/labels";

export const RADII = [25, 50, 100, 250, 500];

const CERTS = Object.entries(CERT_OPTIONS);

function CertFilter({ value, onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-11 bg-white font-normal" data-testid="people-cert-filter-button">
          <Award size={15} />{value.length ? `Certificates (${value.length})` : "Any certificate"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="end" data-testid="people-cert-filter-panel">
        <p className="text-xs text-slate-500 mb-2">Technicians with all selected, still-valid certificates</p>
        <div className="space-y-2">
          {CERTS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={value.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`people-cert-${k}`} />{l}
            </label>
          ))}
        </div>
        {value.length > 0 && <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => onChange([])} data-testid="people-cert-clear">Clear</Button>}
      </PopoverContent>
    </Popover>
  );
}

export function PersonCard({ p, index = 0, right }) {
  return (
    <div data-testid={`person-card-${p.user_id}`} style={{ animationDelay: `${index * 40}ms` }} className="rise panel p-5 flex gap-4 hover:border-sky-400 transition-colors">
      <Link to={`/u/${p.user_id}`}><UserAvatar user={p} size={52} /></Link>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/u/${p.user_id}`} className="font-bold text-slate-900 hover:text-sky-700" data-testid={`person-link-${p.user_id}`}>{p.name}</Link>
          <VipBadge user={p} />
        </div>
        <p className="text-sm text-slate-600 truncate">{p.is_support ? "Support" : p.role === "company" ? "Company" : p.headline || "Technician"}</p>
        {p.location && (
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={12} />{p.location}
            {p.distance_km != null && <span className="ml-1 font-mono text-sky-700" data-testid={`person-distance-${p.user_id}`}>· {p.distance_km} km</span>}
          </p>
        )}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {p.role === "company" && <Pill cls="bg-slate-900 text-amber-400 border-slate-900">Company</Pill>}
          {(p.specializations || []).map((s) => <SpecPill key={s} spec={s} />)}
          <AvailabilityPill value={p.availability} />
        </div>
      </div>
      {right}
    </div>
  );
}

export function NearFilter({ near, setNear, radius, setRadius, prefix }) {
  return (
    <div className="flex gap-2 md:w-[340px]">
      <div className="relative flex-1">
        <Navigation size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid={`${prefix}-near-input`} className="pl-9 h-11 bg-white" placeholder="Near city (e.g. Sundsvall)" value={near} onChange={(e) => setNear(e.target.value)} />
      </div>
      <Select value={String(radius)} onValueChange={(v) => setRadius(Number(v))}>
        <SelectTrigger data-testid={`${prefix}-radius-select`} className="w-[110px] h-11 bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>{RADII.map((r) => <SelectItem key={r} value={String(r)}>{r} km</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function People() {
  const api = useApi();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [role, setRole] = useState(params.get("role") || "all");
  const [spec, setSpec] = useState(params.get("specialization") || "all");
  const [near, setNear] = useState(params.get("near") || "");
  const [radius, setRadius] = useState(Number(params.get("radius")) || 100);
  const [certs, setCerts] = useState([]);
  const [people, setPeople] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (role !== "all") p.set("role", role);
    if (spec !== "all") p.set("specialization", spec);
    if (certs.length) p.set("certs", certs.join(","));
    if (near.trim()) { p.set("near", near.trim()); p.set("radius_km", radius); }
    const t = setTimeout(() => api("get", `/users?${p}`).then((d) => { setPeople(d); setError(""); })
      .catch((e) => { setPeople([]); setError(errMsg(e)); }), near.trim() ? 900 : 300);
    return () => clearTimeout(t);
  }, [q, role, spec, near, radius, certs, api]);

  return (
    <div>
      <p className="eyebrow">Community</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Technicians & companies</h1>
      <div className="mt-8 flex flex-col xl:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="people-search-input" className="pl-9 h-11 bg-white" placeholder="Search: name, skill, turbine..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <NearFilter near={near} setNear={setNear} radius={radius} setRadius={setRadius} prefix="people" />
        <div className="flex gap-3 flex-wrap">
          <Tabs value={role} onValueChange={setRole}>
            <TabsList className="h-11">
              <TabsTrigger value="all" data-testid="people-tab-all">All</TabsTrigger>
              <TabsTrigger value="technician" data-testid="people-tab-technician">Technicians</TabsTrigger>
              <TabsTrigger value="company" data-testid="people-tab-company">Companies</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={spec} onValueChange={setSpec}>
            <SelectTrigger data-testid="people-filter-specialization" className="md:w-52 h-11 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any specialization</SelectItem>
              {Object.entries(SPECS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          {role !== "company" && <CertFilter value={certs} onChange={setCerts} />}
        </div>
      </div>
      <div className="mt-6">
        {people === null ? <Loader /> : people.length === 0 ? <EmptyState testId="people-empty" title={error || "No one found"} text={near && !error ? "Try a larger radius." : undefined} /> : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="people-list">
            {people.map((p, i) => <PersonCard key={p.user_id} p={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
